import { Icon } from '@app/components'
import { DisplaySettings } from '@app/components/display'
import { ReportProblemButton } from '@app/components/ReportProblem'
import {
  Brand,
  ContactEmail,
  SiteAddress,
  SiteBanner,
  SocialLinks,
  ThemeToggle,
  useSetting,
  useShopEnabled,
} from '@app/components/site'
import { LiveAlertBanner } from '@app/features/festival/alerts'
import { NewsletterSignup } from '@app/features/marketing'
import { useScholarshipsLink } from '@app/features/scholarships'
import { useAuth } from '@app/lib/auth'
import { useRouteFocus } from '@app/lib/hooks'
import { InstallBanner } from '@app/pwa/InstallBanner'
import { useEffect, useRef, useState } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router'

const NAV = [
  { to: '/events', label: 'Events' },
  { to: '/programs', label: 'Programs' },
  { to: '/map', label: 'Map' },
  { to: '/schedule', label: 'Schedule' },
  { to: '/scholarships', label: 'Scholarships' },
  { to: '/volunteer', label: 'Volunteer' },
  { to: '/love', label: 'Love Letters' },
  { to: '/shop', label: 'Shop' },
  { to: '/resources', label: 'Resources' },
  { to: '/donate', label: 'Donate', menuOnly: true },
]

const TABS = [
  { to: '/', label: 'Home', icon: 'home', end: true },
  { to: '/map', label: 'Map', icon: 'map' },
  { to: '/schedule', label: 'Schedule', icon: 'calendar' },
  { to: '/donate', label: 'Donate', icon: 'heart' },
  { to: '/portal', label: 'Me', icon: 'user' },
]

export function PublicShell() {
  const { user, can } = useAuth()
  const location = useLocation()
  const main = useRef<HTMLElement>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const shopEnabled = useShopEnabled()
  const mutualAid = useSetting('mutual_aid.enabled') === true
  // One open scholarship: nav and footer go straight to it.
  const scholarshipsHref = useScholarshipsLink()
  const nav = NAV.filter((n) => n.to !== '/shop' || shopEnabled).map((n) =>
    n.to === '/scholarships' ? { ...n, to: scholarshipsHref } : n,
  )
  useRouteFocus(main)
  // Close the small-screen menu whenever the route changes or Escape is pressed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs on navigation
  useEffect(() => setMenuOpen(false), [location.pathname])
  useEffect(() => {
    if (!menuOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [menuOpen])
  return (
    <div className="public-shell">
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <div className="pride-bar" aria-hidden="true" />
      <LiveAlertBanner />
      <SiteBanner />
      <header className="public-header">
        <div className="container public-header__inner">
          <Link to="/" className="brand-link" aria-label="BTown Pride home">
            <Brand />
          </Link>
          <nav
            id="primary-nav"
            className={`nav-links${menuOpen ? ' is-open' : ''}`}
            aria-label="Primary"
          >
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setMenuOpen(false)}
                className={'menuOnly' in n && n.menuOnly ? 'nav-links__menu-only' : undefined}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="cluster">
            <Link to="/donate" className="btn btn--gradient btn--sm hide-mobile">
              Donate
            </Link>
            {user ? (
              <Link
                to={can('admin.access') ? '/admin' : '/portal'}
                className="btn btn--secondary btn--sm"
              >
                {can('admin.access') ? 'Admin' : 'My portal'}
              </Link>
            ) : (
              <Link
                to={`/sign-in?redirectTo=${encodeURIComponent(location.pathname)}`}
                className="btn btn--secondary btn--sm"
              >
                Sign in
              </Link>
            )}
            <button
              type="button"
              className="btn btn--ghost btn--icon nav-menu__btn"
              aria-expanded={menuOpen}
              aria-controls="primary-nav"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setMenuOpen((o) => !o)}
            >
              <Icon name={menuOpen ? 'x' : 'menu'} />
            </button>
          </div>
        </div>
      </header>
      <main id="main" className="public-main" ref={main} tabIndex={-1}>
        <Outlet />
      </main>
      <footer className="public-footer">
        <div className="container public-footer__grid">
          <div>
            <strong>BTown Pride</strong>
            <p className="mb-0">
              Celebrating and supporting LGBTQ+ people in Burlington, Iowa — all year long.
              <br />
              <SiteAddress />
              <br />
              <ContactEmail />
            </p>
            <SocialLinks />
          </div>
          <nav aria-label="Get involved">
            <strong>Get involved</strong>
            <ul>
              <li>
                <Link to="/participate">Become a vendor</Link>
              </li>
              <li>
                <Link to="/participate">Join the parade</Link>
              </li>
              <li>
                <Link to="/volunteer">Volunteer</Link>
              </li>
              <li>
                <Link to="/sponsors">Sponsor Pride</Link>
              </li>
              <li>
                <Link to="/board">Join our board</Link>
              </li>
              <li>
                <Link to="/photos">Photo wall</Link>
              </li>
              <li>
                <Link to="/2027">2027: get notified</Link>
              </li>
            </ul>
          </nav>
          <nav aria-label="Support">
            <strong>Support</strong>
            <ul>
              <li>
                <Link to="/donate">Donate</Link>
              </li>
              <li>
                <Link to="/campaigns">Campaigns</Link>
              </li>
              <li>
                <Link to="/donors">Our supporters</Link>
              </li>
              <li>
                <Link to={scholarshipsHref}>Scholarships</Link>
              </li>
              {shopEnabled ? (
                <li>
                  <Link to="/shop">Shop</Link>
                </li>
              ) : null}
              <li>
                <Link to="/resources">Community resources</Link>
              </li>
              {mutualAid ? (
                <li>
                  <Link to="/mutual-aid">Mutual aid</Link>
                </li>
              ) : null}
            </ul>
          </nav>
          <div>
            <strong>Account</strong>
            <ul>
              <li>
                <Link to="/portal">My portal</Link>
              </li>
              {can('admin.access') ? (
                <li>
                  <Link to="/admin">Admin</Link>
                </li>
              ) : null}
              <li>
                <ReportProblemButton />
              </li>
            </ul>
            <ThemeToggle />
            <DisplaySettings />
            <p className="tiny faint mb-0">© {new Date().getFullYear()} BTown Pride</p>
          </div>
        </div>
        <div className="container">
          <div className="public-footer__news">
            <NewsletterSignup />
          </div>
        </div>
      </footer>
      <nav className="tabbar" aria-label="Quick navigation">
        {TABS.map((t) => (
          <NavLink key={t.to} to={t.to} end={t.end}>
            <Icon name={t.icon} />
            {t.label}
          </NavLink>
        ))}
      </nav>
      <InstallBanner />
    </div>
  )
}
